import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { translations } from '../locales/translations';
import { autoTranslate } from '../locales/autoTranslate';
import { useDomAutoTranslate } from '../locales/DomAutoTranslator';
import { useAuth } from './AuthContext';

const LanguageContext = createContext(null);

const SUPPORTED = ['es', 'en', 'pt', 'fr'];
const normalizeLanguage = value => {
  if (typeof value !== 'string') return null;
  const code = value.trim().toLowerCase().split(/[-_]/)[0];
  return SUPPORTED.includes(code) ? code : null;
};

// Map country code (ISO 3166-1 alpha-2) to our supported language
const COUNTRY_TO_LANG = {
  // Spanish
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', PE: 'es', CL: 'es', VE: 'es',
  EC: 'es', GT: 'es', CU: 'es', BO: 'es', DO: 'es', HN: 'es', PY: 'es',
  SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es', PR: 'es',
  // Portuguese
  BR: 'pt', PT: 'pt', AO: 'pt', MZ: 'pt',
  // French
  FR: 'fr', BE: 'fr', LU: 'fr', MC: 'fr', SN: 'fr', CI: 'fr',
  CM: 'fr', ML: 'fr', BF: 'fr', NE: 'fr', TG: 'fr', BJ: 'fr',
  // English (default for everything else)
  US: 'en', GB: 'en', CA: 'en', AU: 'en', NZ: 'en', IE: 'en',
  IN: 'en', ZA: 'en', PH: 'en', SG: 'en',
};

// 1) Browser language detection
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  return normalizeLanguage(browserLang);
};

// 2) IP geolocation fallback (uses free country.is — no API key, no rate limit)
const detectIPCountry = async () => {
  const ctrl = new AbortController();
  const timeout = setTimeout(() => ctrl.abort(), 2500);
  try {
    const r = await fetch('https://api.country.is/', { signal: ctrl.signal });
    if (!r.ok) return null;
    const j = await r.json();
    const cc = (j.country || '').toUpperCase();
    return COUNTRY_TO_LANG[cc] || null;
  } catch (_) {
    return null;
  } finally {
    clearTimeout(timeout);
  }
};

export function LanguageProvider({ children }) {
  const { user, updateSettings } = useAuth();

  // Synchronous initial: localStorage > browser. (IP runs async after mount.)
  const getInitialLanguage = () =>
    normalizeLanguage(localStorage.getItem('language')) ||
    normalizeLanguage(user?.language) || detectBrowserLanguage() || 'en';

  const [language, setLanguageState] = useState(getInitialLanguage);
  const preferenceRevision = useRef(0);

  // Country is a last resort; never override a browser, account or manual choice.
  useEffect(() => {
    const stored = normalizeLanguage(localStorage.getItem('language'));
    if (stored || normalizeLanguage(user?.language) || detectBrowserLanguage() ||
        localStorage.getItem('ip_lang_checked')) return;
    let cancelled = false;
    const startedRevision = preferenceRevision.current;
    (async () => {
      const fromIP = normalizeLanguage(await detectIPCountry());
      if (cancelled || startedRevision !== preferenceRevision.current) return;
      localStorage.setItem('ip_lang_checked', '1');
      if (fromIP) {
        setLanguageState(fromIP);
        localStorage.setItem('language', fromIP);
        localStorage.setItem('ip_lang_set', '1');
      }
    })();
    return () => { cancelled = true; };
  }, [user?.language]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Sync user.language → localStorage on login (applies when no local pref
  // or when the local pref was only an IP-geolocation guess)
  useEffect(() => {
    const profileLanguage = normalizeLanguage(user?.language);
    if (!profileLanguage) return;
    const stored = localStorage.getItem('language');
    const ipProvisional = localStorage.getItem('ip_lang_set') === '1';
    if (!normalizeLanguage(stored) || ipProvisional) {
      preferenceRevision.current += 1;
      setLanguageState(profileLanguage);
      localStorage.setItem('language', profileLanguage);
      localStorage.removeItem('ip_lang_set');
    }
  }, [user?.language]);

  // Set language and persist
  const setLanguage = async (value) => {
    const lang = normalizeLanguage(value);
    if (!lang) return;
    preferenceRevision.current += 1;
    setLanguageState(lang);
    localStorage.removeItem('ip_lang_set');
    localStorage.setItem('language', lang);
    if (user) {
      try {
        await updateSettings({ language: lang });
      } catch (e) {
        console.error('Error updating language:', e);
      }
    }
  };

  // Translation function
  const t = (key, fallback) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) break;
    }
    if (value === undefined && language !== 'en') {
      value = translations['en'];
      for (const k of keys) {
        value = value?.[k];
        if (value === undefined) break;
      }
    }
    if (value === undefined && language !== 'es') {
      value = translations['es'];
      for (const k of keys) {
        value = value?.[k];
        if (value === undefined) break;
      }
    }
    const result = value || fallback || key;
    // Auto-translate Spanish fallback strings when user's language is not Spanish.
    // This handles hardcoded Spanish strings throughout the codebase by mapping
    // them on-the-fly to the user's language using a common-strings dictionary.
    if (typeof result === 'string' && language !== 'es' && value === undefined) {
      return autoTranslate(result, language);
    }
    return result;
  };

  const supportedLanguages = [
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' }
  ];

  // Activate DOM auto-translator (safety net for hardcoded Spanish strings)
  useDomAutoTranslate(language);

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage,
      t,
      supportedLanguages,
      detectBrowserLanguage
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
